import { AxiosError } from 'axios';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  PageContainer,
  Container,
  Button,
  majorScale,
  Grid,
  Card,
  Heading,
  IDialogProps,
  Dialog,
  FormField,
  InputGroup,
  Tag,
  Popover,
  Menu,
  MenuItem,
  MenuDivider,
  useToast,
} from '../../npaper';
import { CurrencySelect } from '../../npaper/CurrencySelect';
import { useStores } from '../../stores';
import {
  IBankDetails,
  IBankDetailsSnapshotIn,
} from '../../stores/BankDetailsStore';

export const BankDetailsPage = observer(() => {
  const history = useHistory();
  const intl = useIntl();

  const { bankDetailsStore } = useStores();

  useEffect(() => {
    bankDetailsStore.invalidateCaches();
    bankDetailsStore.getAll();
  }, [bankDetailsStore]);

  const [showDialog, setShowDialog] = useState(false);
  const [editBankDetails, setEditBankDetails] = useState<IBankDetails>();

  return (
    <PageContainer>
      <BankDetailsDialog
        isShown={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditBankDetails(undefined);
        }}
        bankDetails={editBankDetails}
      />
      <Container>
        <Button
          iconLeft="arrow-left"
          marginBottom={majorScale(2)}
          onClick={() =>
            history.length > 0 ? history.goBack() : history.push('/settings')
          }
        >
          Indstillinger
        </Button>

        <Grid>
          <Card className="col-span-12 md:col-span-12 lg:col-span-10 xl:col-span-8">
            <div className="dialog">
              <Box className="header">
                <Heading size={400}>Bankkonti</Heading>
              </Box>
              <Grid padding={majorScale(2)}>
                <div className="col-span-6 col-start-4">
                  {bankDetailsStore.items.length === 0 ? (
                    <Box>
                      <p className="medium">
                        Du har ikke oprettet nogen bank konti endnu.
                      </p>
                      <p>
                        For at vi kan sende en faktura til dine kunder, skal du
                        oplyse os om dine bankkonti.
                      </p>
                      <p className="mt-2">
                        Sælger du eksempelvis både i DKK og EUR, skal du oprette
                        en konto til DKK og en til EUR. Den rette konto bliver
                        automatisk valgt når du laver en faktura.
                      </p>
                    </Box>
                  ) : (
                    bankDetailsStore.items.map((details) => (
                      <Card
                        key={details.id}
                        backgroundColor="white"
                        padding={majorScale(1)}
                        display="flex"
                        alignItems="center"
                        marginTop={majorScale(1)}
                      >
                        <Box>
                          <Tag intent="success" className="font-mono">
                            {details.currency}
                          </Tag>
                        </Box>
                        <Box>
                          <p>{details.iban}</p>
                          <p className="small muted">{details.bic}</p>
                        </Box>
                        <Box marginLeft="auto">
                          <Popover
                            content={(close) => (
                              <Menu>
                                <MenuItem
                                  icon="edit"
                                  text={intl.formatMessage({
                                    id: 'defaults.edit',
                                    defaultMessage: 'Edit',
                                  })}
                                  onClick={() => {
                                    close();
                                    setEditBankDetails(details);
                                    setShowDialog(true);
                                  }}
                                />
                                <MenuDivider />
                                <MenuItem
                                  icon="trash"
                                  intent="danger"
                                  text={intl.formatMessage({
                                    id: 'defaults.delete',
                                    defaultMessage: 'Delete',
                                  })}
                                  onClick={() => {
                                    bankDetailsStore.deleteItem(details);
                                    close();
                                  }}
                                />
                              </Menu>
                            )}
                          >
                            <Button iconLeft="more" minimal />
                          </Popover>
                        </Box>
                      </Card>
                    ))
                  )}
                </div>
                <Box
                  className="col-span-6 col-start-4"
                  marginTop={majorScale(2)}
                >
                  <Button iconLeft="add" onClick={() => setShowDialog(true)}>
                    Tilføj bankoplysninger
                  </Button>
                </Box>
              </Grid>
            </div>
          </Card>
        </Grid>
      </Container>
    </PageContainer>
  );
});

const BankDetailsDialog: React.FC<
  Omit<IDialogProps, 'actions'> & { bankDetails?: IBankDetails }
> = ({ isShown: defaultIsShown, title, bankDetails, ...props }) => {
  const intl = useIntl();
  const { bankDetailsStore } = useStores();
  const toaster = useToast();

  const [isShown, setIsShown] = useState(defaultIsShown);

  const {
    reset,
    register,
    control,
    errors,
    setError,
    handleSubmit,
    formState,
  } = useForm<IBankDetailsSnapshotIn>();

  useEffect(() => {
    if (!isShown) return;
    if (bankDetails) {
      reset(getSnapshot(bankDetails));
    } else {
      reset({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown, bankDetails]);

  useEffect(() => {
    setIsShown(defaultIsShown);
  }, [defaultIsShown]);

  const close = () => {
    setIsShown(false);
  };

  const onSubmit = async (values: IBankDetailsSnapshotIn) => {
    if (bankDetails) {
      try {
        await bankDetailsStore.updateItem(bankDetails, values);
        close();
      } catch (err) {
        handleError(err);
      }
    } else {
      try {
        await bankDetailsStore.createItem(values);
        close();
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handleError = (error: AxiosError) => {
    if (error.response && error.response.status === 400) {
      Object.keys(error.response.data).forEach((key) => {
        setError(key, {
          message: error.response!.data[key].join(','),
        });
      });
    } else {
      toaster.showDefaultError(error);
    }
  };

  return (
    <Dialog
      {...props}
      isShown={isShown}
      title="Opret bankkonto"
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage id="defaults.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {bankDetails ? (
              <FormattedMessage
                id="defaults.save-changes"
                defaultMessage="Save changes"
              />
            ) : (
              <FormattedMessage
                id="settings.bank-details.create.confirm"
                defaultMessage="Save bank details"
              />
            )}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)} paddingTop={majorScale(4)}>
        <FormField
          className="col-span-9"
          inline
          label="Valuta"
          isInvalid={!!errors.currency}
          hint={errors.currency && errors.currency.message}
        >
          <Controller
            control={control}
            name="currency"
            defaultValue="DKK"
            render={({ onChange, value }) => (
              <CurrencySelect
                onChange={onChange}
                value={value}
                restrictToOwn={false}
              />
            )}
          />
        </FormField>
        <FormField className="col-span-9" inline label="IBAN">
          <InputGroup
            placeholder="IBAN"
            isInvalid={!!errors.iban}
            validationMessage={errors.iban && errors.iban.message}
            name="iban"
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please provide an IBAN code',
                description: 'Validation: IBAN code required',
              }),
            })}
          />
        </FormField>
        <FormField className="col-span-9" inline label="BIC/SWIFT">
          <InputGroup
            placeholder="BIC/SWIFT"
            name="bic"
            isInvalid={!!errors.bic}
            validationMessage={errors.bic && errors.bic.message}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please provide a BIC or SWIFT code',
                description: 'Validation: BIC/SWIFT code required',
              }),
            })}
          />
        </FormField>
      </Grid>
    </Dialog>
  );
};
